/**
 * The grader: layers 0 to 1.5 over one take.
 *
 * It is a pure reducer. `startTake` builds the initial state, `applyEvent` folds one
 * performance event in (events must arrive in time order), `advanceTo` lets time pass with no
 * event, and `readTake` reads the judged take at any moment without changing anything.
 * `finishTake` ends the take: whatever was still pending is settled. Live grading and batch
 * grading are the same fold, so a recorded take replays to exactly the judgments it earned live.
 *
 * Bindings are made when a press arrives and never revisited; everything else (dimension
 * credits, merges, slices, the pedal stream) is derived from the state on read.
 */

import { Clock, resolveReferenceTempo } from './clock';
import { CreditCurve, resolveCurve } from './creditCurve';
import {
  ExtraNote,
  ExtraNoteCount,
  JudgedTake,
  NoteJudgment,
  NoteStatus,
  PressReading,
} from './judgedTake';
import {
  BindingWindow,
  Candidate,
  KEY_CHATTER_MS,
  MatchingConfig,
  MatchingRule,
  bindingWindow,
  rankCandidates,
  ruleForNote,
} from './matching';
import {
  DimensionOutcome,
  DimensionRule,
  NoteJudgmentConfig,
  judgeDimension,
  mergeNote,
  noteJudgmentErrors,
  withoutDimensions,
} from './noteJudgment';
import {
  NO_SUSTAIN,
  PedalStreamConfig,
  SustainState,
  applyPedalEvent,
  judgePedalStream,
  pedalStreamErrors,
} from './pedal';
import { INDEPENDENT_SLICES, SliceConfig, judgeSlices, sliceConfigErrors } from './slices';
import {
  CLOCK_DIMENSIONS,
  DIMENSIONS,
  Dimension,
  GradingConfigError,
  HANDS,
  Hand,
  PerformanceEvent,
  PressEvent,
  RealizedScore,
  ReleaseEvent,
  ScoreNote,
} from './types';

/** Layers 0 to 1.5 of a preset: how presses are matched and how each note is judged. */
export interface JudgmentConfig {
  readonly matching: MatchingConfig;
  readonly note: NoteJudgmentConfig;
  readonly slices?: SliceConfig;
  /** Grade the pedal stream against the score's pedal marks. */
  readonly pedal?: PedalStreamConfig;
}

/** What the current input can detect. Anything it cannot is removed from every judgment. */
export interface CapabilityMask {
  readonly undetectable: readonly (Dimension | 'pedal')[];
}

export interface TakeSetup {
  readonly score: RealizedScore;
  readonly judgment: JudgmentConfig;
  /**
   * Drives time-window matching. May be null only when every region uses order matching. When
   * every graded note uses order matching, a clock never limits binding.
   */
  readonly clock: Clock | null;
  /**
   * The set tempo that turns notated lengths into time under order matching: the preset's,
   * else the session's (see `resolveReferenceTempo`). Without one, the score's notated tempo is
   * used; without that, duration is not judged there.
   */
  readonly referenceTempoBpm?: number | null;
  /** The hands the player plays; only these are graded. Default: both. */
  readonly gradedHands?: readonly Hand[];
  /** The input's capability mask. */
  readonly mask?: CapabilityMask;
  /** Dimensions the player, teacher or coach switched off for now (live narrowing). */
  readonly narrowedAway?: readonly Dimension[];
}

interface Binding {
  readonly press: PressReading;
  readonly releaseMs: number | null;
  readonly releasedUnderSustain: boolean;
}

interface NoteState {
  readonly note: ScoreNote;
  readonly index: number;
  readonly rule: MatchingRule;
  readonly graded: boolean;
  /** Null under order matching. */
  readonly window: BindingWindow | null;
  readonly binding: Binding | null;
  readonly charges: readonly PressReading[];
  /**
   * The last moment an extra press could still be charged to this note: its binding window,
   * its notated length, or the gap before the next note comes due. Null under order matching.
   */
  readonly chargeableUntilMs: number | null;
}

/** Who a held key belongs to, so its release lands on the right note. */
type HeldKey = { readonly noteIndex: number } | { readonly extraIndex: number };

export interface TakeState {
  readonly setup: TakeSetup;
  /** The judgment config after the capability mask and live narrowing. */
  readonly effective: NoteJudgmentConfig;
  readonly notes: readonly NoteState[];
  readonly extras: readonly ExtraNote[];
  readonly sustain: SustainState;
  readonly held: Readonly<Record<number, HeldKey>>;
  readonly lastPress: Readonly<Record<number, PressReading>>;
  readonly scopeResetsMs: readonly number[];
  readonly nowMs: number;
  readonly finished: boolean;
  /** The span of the piece on the clock, for charging extras between notes. */
  readonly pieceSpan: TimeSpan | null;
  /** The setup's reference tempo, else the score's notated tempo; null when neither is set. */
  readonly referenceTempoBpm: number | null;
  /**
   * When each order-matching region is open on the clock. Only a timed take has them: one with a
   * clock and at least one graded time-window note.
   */
  readonly orderRegionSpans: Readonly<Record<string, TimeSpan>>;
}

interface TimeSpan {
  readonly startMs: number;
  readonly endMs: number;
}

function regionKey(note: ScoreNote): string {
  return note.region ?? '';
}

/**
 * With order matching as the default rule, no region may use time-window matching. The notes
 * outside every named region would then form one order region spanning the whole piece, so an
 * early skipped note would read missed only once the clock passed the last of them. Refused
 * until a preset needs the shape (it would key each run of default-region notes separately).
 */
export function matchingRegionErrors(matching: MatchingConfig): string[] {
  const regionRules = matching.regionRules ?? {};
  const timedRegions = Object.keys(regionRules).filter(
    (region) => regionRules[region] === 'time-window',
  );
  return matching.rule === 'order' && timedRegions.length > 0
    ? [
        `matching: with order as the default rule, no region may use time-window matching (got ${timedRegions.join(', ')})`,
      ]
    : [];
}

/** Every validity problem with a judgment config; empty when valid. */
export function judgmentConfigErrors(config: JudgmentConfig): string[] {
  const extra = config.matching.extraNotes;
  const extraErrors =
    extra.counts && extra.charge !== 'own-error' && !(extra.credit >= 0 && extra.credit <= 1)
      ? ['extra notes: credit must be between 0 and 1']
      : [];
  return [
    ...noteJudgmentErrors(config.note),
    ...matchingRegionErrors(config.matching),
    ...extraErrors,
    ...(config.slices ? sliceConfigErrors(config.slices) : []),
    ...(config.pedal ? pedalStreamErrors(config.pedal) : []),
  ];
}

export function validateJudgmentConfig(config: JudgmentConfig): void {
  const errors = judgmentConfigErrors(config);
  if (errors.length > 0) {
    throw new GradingConfigError(errors.join('; '));
  }
}

/** The note judgment actually in force: the preset's, minus the mask and live narrowing. */
export function effectiveNoteJudgment(
  config: NoteJudgmentConfig,
  mask: CapabilityMask | undefined,
  narrowedAway: readonly Dimension[] | undefined,
): NoteJudgmentConfig {
  const masked = DIMENSIONS.filter(
    (dimension) => (mask?.undetectable ?? []).indexOf(dimension) >= 0,
  );
  return withoutDimensions(config, [...masked, ...(narrowedAway ?? [])]);
}

function scoreOrder(a: ScoreNote, b: ScoreNote): number {
  return a.onsetBeat - b.onsetBeat || a.pitch - b.pitch;
}

/** Builds the state for a new take. Throws {@link GradingConfigError} on an invalid setup. */
export function startTake(setup: TakeSetup): TakeState {
  validateJudgmentConfig(setup.judgment);
  const gradedHands = setup.gradedHands ?? HANDS;
  const ordered = [...setup.score.notes].sort(scoreOrder);
  const badNote = ordered.find((note) => !(note.offsetBeat > note.onsetBeat));
  if (badNote) {
    throw new GradingConfigError(`note ${badNote.id}: offset must come after onset`);
  }
  const notes = ordered.map((note, index): NoteState => {
    const rule = ruleForNote(setup.judgment.matching, note);
    if (rule === 'time-window' && setup.clock === null) {
      throw new GradingConfigError('time-window matching needs a clock');
    }
    return {
      note,
      index,
      rule,
      graded: gradedHands.indexOf(note.hand) >= 0,
      window: rule === 'time-window' && setup.clock ? bindingWindow(setup.clock, note) : null,
      binding: null,
      charges: [],
      chargeableUntilMs: null,
    };
  });
  const clock = setup.clock;
  // An all-order take binds for as long as it runs: the clock bounds an order region only when
  // the take also has graded notes on the clock.
  const timed = notes.some((noteState) => noteState.graded && noteState.rule === 'time-window');
  const regionClock = timed ? clock : null;
  const orderRegionSpans = regionClock
    ? notes
        .filter((noteState) => noteState.rule === 'order')
        .reduce<Record<string, TimeSpan>>((spans, noteState) => {
          const window = bindingWindow(regionClock, noteState.note);
          const key = regionKey(noteState.note);
          const known = spans[key];
          const span = known
            ? {
                startMs: Math.min(known.startMs, window.startMs),
                endMs: Math.max(known.endMs, window.endMs),
              }
            : { startMs: window.startMs, endMs: window.endMs };
          return { ...spans, [key]: span };
        }, {})
    : {};
  const pieceSpan =
    clock && ordered.length > 0
      ? {
          startMs: bindingWindow(clock, ordered[0]).startMs,
          endMs: Math.max(...ordered.map((note) => clock.msAtBeat(note.offsetBeat))),
        }
      : null;
  return {
    setup,
    effective: effectiveNoteJudgment(setup.judgment.note, setup.mask, setup.narrowedAway),
    notes: clock ? withChargeableSpans(notes, clock, pieceSpan) : notes,
    extras: [],
    sustain: NO_SUSTAIN,
    held: {},
    lastPress: {},
    scopeResetsMs: [],
    nowMs: Number.NEGATIVE_INFINITY,
    finished: false,
    pieceSpan,
    referenceTempoBpm: resolveReferenceTempo({
      presetTempoBpm: setup.referenceTempoBpm ?? undefined,
      notatedTempoBpm: setup.score.notatedTempoBpm,
    }),
    orderRegionSpans,
  };
}

function withChargeableSpans(
  notes: readonly NoteState[],
  clock: Clock,
  pieceSpan: TimeSpan | null,
): NoteState[] {
  const dues = notes
    .filter((noteState) => noteState.graded && noteState.window !== null)
    .map((noteState) => (noteState.window as BindingWindow).dueMs)
    .sort((a, b) => a - b);
  return notes.map((noteState) => {
    if (noteState.window === null) {
      return noteState;
    }
    const offsetMs = clock.msAtBeat(noteState.note.offsetBeat);
    const nextDue = dues.find((dueMs) => dueMs > offsetMs);
    const gapEndMs = nextDue ?? (pieceSpan ? pieceSpan.endMs : offsetMs);
    return {
      ...noteState,
      chargeableUntilMs: Math.max(noteState.window.endMs, offsetMs, gapEndMs),
    };
  });
}

/** Whether a later extra press could still lower this note's credit. */
function openToCharges(state: TakeState, noteState: NoteState, credit: number): boolean {
  const rule = state.setup.judgment.matching.extraNotes;
  if (
    state.finished ||
    !rule.counts ||
    rule.charge === 'own-error' ||
    noteState.chargeableUntilMs === null
  ) {
    return false;
  }
  const alreadyCapped =
    rule.charge === 'capped' && (noteState.charges.length > 0 || credit <= rule.credit);
  return !alreadyCapped && credit > 0 && state.nowMs <= noteState.chargeableUntilMs;
}

function isOpenOrderRegionNote(state: TakeState, noteState: NoteState, timeMs: number): boolean {
  if (noteState.rule !== 'order' || !noteState.graded || noteState.binding !== null) {
    return false;
  }
  const span = state.orderRegionSpans[regionKey(noteState.note)];
  return span === undefined || (timeMs >= span.startMs && timeMs <= span.endMs);
}

/** Under order matching the candidates are the earliest unmatched slice of each open region. */
function orderCandidates(state: TakeState, timeMs: number): NoteState[] {
  const open = state.notes.filter((noteState) => isOpenOrderRegionNote(state, noteState, timeMs));
  const earliestByRegion = open.reduce<Record<string, number>>((earliest, noteState) => {
    const key = regionKey(noteState.note);
    const current = earliest[key];
    return current === undefined || noteState.note.onsetBeat < current
      ? { ...earliest, [key]: noteState.note.onsetBeat }
      : earliest;
  }, {});
  return open.filter(
    (noteState) => earliestByRegion[regionKey(noteState.note)] === noteState.note.onsetBeat,
  );
}

function timeWindowCandidates(state: TakeState, timeMs: number): NoteState[] {
  return state.notes.filter(
    (noteState) =>
      noteState.graded &&
      noteState.window !== null &&
      timeMs >= noteState.window.startMs &&
      timeMs <= noteState.window.endMs,
  );
}

function toCandidate(noteState: NoteState): Candidate {
  return {
    index: noteState.index,
    pitch: noteState.note.pitch,
    matched: noteState.binding !== null,
    dueMs: noteState.window ? noteState.window.dueMs : null,
  };
}

/** Where a press with no candidate is charged: the note sounding then, else the nearest in time. */
function chargeTargetWithoutCandidates(state: TakeState, press: PressReading): NoteState | null {
  const clock = state.setup.clock;
  const span = state.pieceSpan;
  if (clock === null || span === null || press.timeMs < span.startMs || press.timeMs > span.endMs) {
    return null;
  }
  const graded = state.notes.filter((noteState) => noteState.graded && noteState.window !== null);
  const sounding = graded.filter(
    (noteState) =>
      press.timeMs >= (noteState.window as BindingWindow).startMs &&
      press.timeMs <= clock.msAtBeat(noteState.note.offsetBeat),
  );
  const pool = sounding.length > 0 ? sounding : graded;
  const query = { timeMs: press.timeMs, pitch: press.pitch, pitchMatters: sounding.length > 0 };
  const distanceInTime = (noteState: NoteState): number => {
    const startMs = (noteState.window as BindingWindow).dueMs;
    const endMs = clock.msAtBeat(noteState.note.offsetBeat);
    return press.timeMs < startMs ? startMs - press.timeMs : Math.max(0, press.timeMs - endMs);
  };
  const nearest =
    sounding.length > 0
      ? pool
      : pool.filter(
          (noteState) => distanceInTime(noteState) === Math.min(...pool.map(distanceInTime)),
        );
  const winner = rankCandidates(query, nearest.map(toCandidate));
  return winner === null ? null : state.notes[winner.index];
}

function extraCount(state: TakeState, target: NoteState | null): ExtraNoteCount {
  const rule = state.setup.judgment.matching.extraNotes;
  if (!rule.counts || target === null) {
    return 'nothing';
  }
  if (rule.charge === 'own-error') {
    return 'own-error';
  }
  return rule.charge === 'capped' && target.charges.length > 0 ? 'nothing' : 'held-note';
}

function replaceNote(notes: readonly NoteState[], updated: NoteState): NoteState[] {
  return notes.map((noteState) => (noteState.index === updated.index ? updated : noteState));
}

function isKeyChatter(state: TakeState, press: PressEvent): boolean {
  const last = state.lastPress[press.pitch];
  return (
    last !== undefined &&
    last.velocity === press.velocity &&
    press.timeMs - last.timeMs <= KEY_CHATTER_MS
  );
}

function chargeExtra(state: TakeState, press: PressReading, target: NoteState | null): TakeState {
  const extra: ExtraNote = {
    press,
    chargedTo: target ? target.note.id : null,
    hand: target ? target.note.hand : null,
    countsAs: extraCount(state, target),
  };
  const notes = target
    ? replaceNote(state.notes, { ...target, charges: [...target.charges, press] })
    : state.notes;
  return {
    ...state,
    notes,
    extras: [...state.extras, extra],
    held: { ...state.held, [press.pitch]: { extraIndex: state.extras.length } },
  };
}

function applyPress(state: TakeState, event: PressEvent): TakeState {
  const press: PressReading = {
    timeMs: event.timeMs,
    pitch: event.pitch,
    velocity: event.velocity,
    ...(event.finger !== undefined ? { finger: event.finger } : {}),
  };
  if (isKeyChatter(state, event)) {
    return state;
  }
  const withPress = { ...state, lastPress: { ...state.lastPress, [event.pitch]: press } };
  const candidates = [
    ...timeWindowCandidates(state, event.timeMs),
    ...orderCandidates(state, event.timeMs),
  ];
  const query = {
    timeMs: event.timeMs,
    pitch: event.pitch,
    pitchMatters: state.effective.dimensions.pitch !== undefined,
  };
  const winner = rankCandidates(query, candidates.map(toCandidate));
  if (winner === null) {
    return chargeExtra(withPress, press, chargeTargetWithoutCandidates(state, press));
  }
  const target = state.notes[winner.index];
  if (target.binding !== null) {
    return chargeExtra(withPress, press, target);
  }
  const bound: NoteState = {
    ...target,
    binding: { press, releaseMs: null, releasedUnderSustain: false },
  };
  return {
    ...withPress,
    notes: replaceNote(state.notes, bound),
    held: { ...state.held, [event.pitch]: { noteIndex: target.index } },
  };
}

function applyRelease(state: TakeState, event: ReleaseEvent): TakeState {
  const heldKey = state.held[event.pitch];
  const held = { ...state.held };
  delete held[event.pitch];
  if (heldKey === undefined || !('noteIndex' in heldKey)) {
    return { ...state, held };
  }
  const noteState = state.notes[heldKey.noteIndex];
  if (noteState.binding === null || noteState.binding.releaseMs !== null) {
    return { ...state, held };
  }
  const released: NoteState = {
    ...noteState,
    binding: {
      ...noteState.binding,
      releaseMs: event.timeMs,
      releasedUnderSustain: state.sustain.down,
    },
  };
  return { ...state, held, notes: replaceNote(state.notes, released) };
}

/** Folds one performance event into the take. Events must arrive in time order. */
export function applyEvent(state: TakeState, event: PerformanceEvent): TakeState {
  if (state.finished) {
    throw new GradingConfigError('the take has finished; start a new take');
  }
  if (!Number.isFinite(event.timeMs) || event.timeMs < state.nowMs) {
    throw new GradingConfigError(
      `events must arrive in time order (${event.timeMs} < ${state.nowMs})`,
    );
  }
  const advanced = { ...state, nowMs: event.timeMs };
  switch (event.kind) {
    case 'press':
      return applyPress(advanced, event);
    case 'release':
      return applyRelease(advanced, event);
    case 'pedal':
      return { ...advanced, sustain: applyPedalEvent(advanced.sustain, event) };
    case 'scope-reset':
      return { ...advanced, scopeResetsMs: [...advanced.scopeResetsMs, event.timeMs] };
  }
}

/** Lets time pass with no event (notes whose window closes are then missed). */
export function advanceTo(state: TakeState, timeMs: number): TakeState {
  return timeMs > state.nowMs ? { ...state, nowMs: timeMs } : state;
}

/** Ends the take at `endMs`: pending notes are missed, notes not yet due are unreached. */
export function finishTake(state: TakeState, endMs: number): TakeState {
  return { ...advanceTo(state, endMs), finished: true };
}

/** Grades a whole recorded take in one call: the same fold live grading uses. */
export function gradeRecordedTake(
  setup: TakeSetup,
  events: readonly PerformanceEvent[],
  endMs: number,
): TakeState {
  return finishTake(events.reduce(applyEvent, startTake(setup)), endMs);
}

// ---------------------------------------------------------------------------------------------
// Reading the judged take.

/**
 * When the note falls due on the clock (its notated onset). Null under order matching unless the
 * take is timed: there the note's region closes on the clock.
 */
function noteDueMs(state: TakeState, noteState: NoteState): number | null {
  if (noteState.window !== null) {
    return noteState.window.dueMs;
  }
  const clock = state.setup.clock;
  const span = state.orderRegionSpans[regionKey(noteState.note)];
  return clock && span !== undefined ? clock.msAtBeat(noteState.note.onsetBeat) : null;
}

/**
 * An unplayed note under order matching. In a timed take it is missed once the clock has passed
 * its region, and when the take ends, if it was already due (as under time-window matching).
 * Otherwise nothing closes the region: it stays unreached.
 */
function orderNoteStatus(state: TakeState, noteState: NoteState): NoteStatus {
  const span = state.orderRegionSpans[regionKey(noteState.note)];
  const dueMs = noteDueMs(state, noteState);
  if (span === undefined || dueMs === null) {
    return 'unreached';
  }
  if (state.finished) {
    return dueMs <= state.nowMs ? 'missed' : 'unreached';
  }
  return state.nowMs > span.endMs ? 'missed' : 'unreached';
}

function noteStatus(state: TakeState, noteState: NoteState): NoteStatus {
  if (!noteState.graded) {
    return 'not-graded';
  }
  if (noteState.binding !== null) {
    return 'matched';
  }
  if (noteState.window === null) {
    return orderNoteStatus(state, noteState);
  }
  if (state.finished) {
    return noteState.window.dueMs <= state.nowMs ? 'missed' : 'unreached';
  }
  if (state.nowMs > noteState.window.endMs) {
    return 'missed';
  }
  return noteState.window.dueMs <= state.nowMs ? 'pending' : 'unreached';
}

function lastReach(curve: CreditCurve): number {
  return curve.above.length === 0 ? 0 : curve.above[curve.above.length - 1].distance;
}

interface NoteTiming {
  readonly msPerBeat: number | null;
  readonly notatedOnsetMs: number | null;
  readonly notatedOffsetMs: number | null;
  readonly notatedLengthMs: number | null;
}

function noteTiming(state: TakeState, noteState: NoteState): NoteTiming {
  const clock = state.setup.clock;
  const note = noteState.note;
  if (noteState.rule === 'time-window' && clock) {
    const onsetMs = clock.msAtBeat(note.onsetBeat);
    const offsetMs = clock.msAtBeat(note.offsetBeat);
    return {
      msPerBeat: clock.msPerBeatAt(note.onsetBeat),
      notatedOnsetMs: onsetMs,
      notatedOffsetMs: offsetMs,
      notatedLengthMs: offsetMs - onsetMs,
    };
  }
  const tempo = state.referenceTempoBpm;
  const msPerBeat = tempo === null ? null : 60000 / tempo;
  return {
    msPerBeat,
    notatedOnsetMs: null,
    notatedOffsetMs: null,
    notatedLengthMs: msPerBeat === null ? null : (note.offsetBeat - note.onsetBeat) * msPerBeat,
  };
}

/** A deviation in ms expressed in the curve's unit; null when the unit cannot be formed. */
function inCurveUnit(curve: CreditCurve, deviationMs: number, timing: NoteTiming): number | null {
  if (curve.unit === 'ms') {
    return deviationMs;
  }
  if (curve.unit === 'beats') {
    return timing.msPerBeat === null ? null : deviationMs / timing.msPerBeat;
  }
  if (curve.unit === 'proportion') {
    return timing.notatedLengthMs ? deviationMs / timing.notatedLengthMs : null;
  }
  return null;
}

/**
 * The rule with its curve resolved for this note (per-side units, floors and caps, a length
 * curve). A curve without per-side settings comes back as it is. Null when the note cannot give
 * what the curve needs (no tempo, no length).
 */
function ruleForNoteTiming(
  rule: DimensionRule,
  note: ScoreNote,
  timing: NoteTiming,
): DimensionRule | null {
  const curve = resolveCurve(rule.curve, {
    msPerBeat: timing.msPerBeat,
    lengthMs: timing.notatedLengthMs,
    lengthBeats: note.offsetBeat - note.onsetBeat,
  });
  if (curve === null) {
    return null;
  }
  return curve === rule.curve ? rule : { ...rule, curve };
}

/**
 * Judges a dimension measured from the key release (offset, duration). While the key is still
 * down the outcome waits; once the deviation is past the curve's last breakpoint it reads as
 * late, provisionally, because a release under the sustain pedal would still suspend it (it
 * settles at once when the input cannot detect the pedal). When the take ends with the key
 * down, the end is the release.
 */
function releaseOutcome(
  state: TakeState,
  rule: DimensionRule | null,
  deviationAtMs: (releaseMs: number) => number,
  timing: NoteTiming,
  binding: Binding,
  clampLength: number | undefined,
): DimensionOutcome {
  if (binding.releaseMs !== null) {
    if (binding.releasedUnderSustain) {
      return { state: 'not-judged', reason: 'pedal' };
    }
    const deviation =
      rule === null ? null : inCurveUnit(rule.curve, deviationAtMs(binding.releaseMs), timing);
    return deviation === null || rule === null
      ? { state: 'not-judged', reason: 'no-reference-tempo' }
      : judgeDimension(rule, deviation, clampLength);
  }
  const soFar = rule === null ? null : inCurveUnit(rule.curve, deviationAtMs(state.nowMs), timing);
  if (soFar === null || rule === null) {
    return { state: 'not-judged', reason: 'no-reference-tempo' };
  }
  if (state.finished) {
    if (state.sustain.down) {
      return { state: 'not-judged', reason: 'pedal' };
    }
    return soFar < rule.curve.plateau[0]
      ? { state: 'not-judged', reason: 'no-reading' }
      : judgeDimension(rule, soFar, clampLength);
  }
  if (soFar <= rule.curve.plateau[1] + lastReach(rule.curve)) {
    return { state: 'pending' };
  }
  const pedalDetectable = (state.setup.mask?.undetectable ?? []).indexOf('pedal') < 0;
  const late = judgeDimension(rule, soFar, clampLength);
  return pedalDetectable ? { ...late, provisional: true } : late;
}

function lengthInUnit(curve: CreditCurve, timing: NoteTiming): number | undefined {
  if (timing.notatedLengthMs === null) {
    return undefined;
  }
  const length = inCurveUnit(curve, timing.notatedLengthMs, timing);
  return length === null ? undefined : length;
}

function judgeOneDimension(
  state: TakeState,
  noteState: NoteState,
  dimension: Dimension,
  binding: Binding,
): DimensionOutcome {
  const rule = state.effective.dimensions[dimension];
  if (rule === undefined) {
    return state.setup.judgment.note.dimensions[dimension] === undefined
      ? { state: 'not-judged', reason: 'preset' }
      : { state: 'not-judged', reason: 'removed' };
  }
  const note = noteState.note;
  const press = binding.press;
  const timing = noteTiming(state, noteState);
  if (noteState.rule === 'order' && CLOCK_DIMENSIONS.indexOf(dimension) >= 0) {
    return { state: 'not-judged', reason: 'order-matching' };
  }
  switch (dimension) {
    case 'pitch':
      return judgeDimension(rule, press.pitch - note.pitch);
    case 'velocity':
      return note.targetVelocity === undefined
        ? { state: 'not-judged', reason: 'no-target' }
        : judgeDimension(rule, press.velocity - note.targetVelocity);
    case 'finger':
      if (note.finger === undefined) {
        return { state: 'not-judged', reason: 'no-target' };
      }
      return press.finger === undefined
        ? { state: 'not-judged', reason: 'no-reading' }
        : judgeDimension(rule, press.finger === note.finger ? 0 : 1);
    case 'onset': {
      const onsetRule = ruleForNoteTiming(rule, note, timing);
      const deviation =
        onsetRule === null
          ? null
          : inCurveUnit(onsetRule.curve, press.timeMs - (timing.notatedOnsetMs as number), timing);
      return deviation === null || onsetRule === null
        ? { state: 'not-judged', reason: 'no-reference-tempo' }
        : judgeDimension(onsetRule, deviation, lengthInUnit(onsetRule.curve, timing));
    }
    case 'offset': {
      const offsetRule = ruleForNoteTiming(rule, note, timing);
      return releaseOutcome(
        state,
        offsetRule,
        (releaseMs) => releaseMs - (timing.notatedOffsetMs as number),
        timing,
        binding,
        offsetRule === null ? undefined : lengthInUnit(offsetRule.curve, timing),
      );
    }
    case 'duration':
      if (timing.notatedLengthMs === null) {
        return { state: 'not-judged', reason: 'no-reference-tempo' };
      }
      return releaseOutcome(
        state,
        ruleForNoteTiming(rule, note, timing),
        (releaseMs) => releaseMs - press.timeMs - (timing.notatedLengthMs as number),
        timing,
        binding,
        undefined,
      );
  }
}

function chargedCredit(state: TakeState, credit: number, charges: number): number {
  const rule = state.setup.judgment.matching.extraNotes;
  if (!rule.counts || rule.charge === 'own-error' || charges === 0) {
    return credit;
  }
  return rule.charge === 'capped'
    ? Math.min(credit, rule.credit)
    : credit * Math.pow(rule.credit, charges);
}

function judgeNote(state: TakeState, noteState: NoteState): NoteJudgment {
  const status = noteStatus(state, noteState);
  const base = {
    noteId: noteState.note.id,
    index: noteState.index,
    hand: noteState.note.hand,
    onsetBeat: noteState.note.onsetBeat,
    groups: noteState.note.groups ?? {},
    rule: noteState.rule,
    status,
    dueMs: noteDueMs(state, noteState),
    extraCharges: noteState.charges.length,
    attempts: noteState.binding
      ? [noteState.binding.press, ...noteState.charges]
      : noteState.charges,
  };
  const binding = noteState.binding;
  if (binding === null) {
    return {
      ...base,
      press: null,
      releaseMs: null,
      releasedUnderSustain: false,
      pitchCorrect: null,
      dimensions: {},
      gatePassed: status !== 'missed',
      credit: status === 'missed' ? 0 : null,
      settled:
        status === 'missed' ||
        status === 'not-graded' ||
        (status === 'unreached' && state.finished),
    };
  }
  const dimensions = DIMENSIONS.reduce<Partial<Record<Dimension, DimensionOutcome>>>(
    (outcomes, dimension) => {
      const inPreset = state.setup.judgment.note.dimensions[dimension] !== undefined;
      return inPreset
        ? { ...outcomes, [dimension]: judgeOneDimension(state, noteState, dimension, binding) }
        : outcomes;
    },
    {},
  );
  const merged = mergeNote(state.effective, dimensions);
  const credit = chargedCredit(state, merged.credit, noteState.charges.length);
  return {
    ...base,
    press: binding.press,
    releaseMs: binding.releaseMs,
    releasedUnderSustain: binding.releasedUnderSustain,
    pitchCorrect: binding.press.pitch === noteState.note.pitch,
    dimensions,
    gatePassed: merged.gatePassed,
    credit,
    settled: merged.settled && !openToCharges(state, noteState, credit),
  };
}

/** Reads the judged take at the current moment. Pure: reading never changes the state. */
export function readTake(state: TakeState): JudgedTake {
  const notes = state.notes.map((noteState) => judgeNote(state, noteState));
  const gradedNotes = state.notes
    .filter((noteState) => noteState.graded)
    .map((noteState) => noteState.note);
  const clock = state.setup.clock;
  const tempo = state.referenceTempoBpm;
  const sliceContext = {
    msPerBeat: (beat: number) =>
      clock ? clock.msPerBeatAt(beat) : tempo === null ? null : 60000 / tempo,
  };
  const slices = state.setup.judgment.slices ?? {};
  const undetectable = state.setup.mask?.undetectable ?? [];
  const releaseDetectable = undetectable.indexOf('offset') < 0;
  const pedalConfig = state.setup.judgment.pedal;
  const pedalDetectable = undetectable.indexOf('pedal') < 0;
  return {
    notes,
    extras: state.extras,
    onsetSlices: judgeSlices(
      'onset',
      gradedNotes,
      notes,
      slices.onset ?? INDEPENDENT_SLICES,
      sliceContext,
    ),
    releaseSlices:
      slices.release && releaseDetectable
        ? judgeSlices('release', gradedNotes, notes, slices.release, sliceContext)
        : [],
    pedal:
      pedalConfig && pedalDetectable && clock && state.setup.score.pedalMarks
        ? judgePedalStream(
            state.setup.score.pedalMarks,
            state.sustain,
            clock,
            pedalConfig,
            state.nowMs,
            state.finished,
          )
        : [],
    scopeResetsMs: state.scopeResetsMs,
    nowMs: state.nowMs,
    finished: state.finished,
  };
}
